import { useState, useEffect } from 'react';
import { useMediaStore } from '../stores/mediaStore';

export const useMediaDevices = () => {
  const { setDevices } = useMediaStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDevices = async () => {
    setLoading(true);
    setError(null);

    try {
      // Проверка доступности API
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          'WebRTC не поддерживается. Используйте HTTPS или localhost для доступа к камере и микрофону.'
        );
      }

      // Запрашиваем разрешения
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });

      // Получаем список устройств
      const devices = await navigator.mediaDevices.enumerateDevices();

      const audioDevices = devices.filter((d) => d.kind === 'audioinput');
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      const audioOutputDevices = devices.filter((d) => d.kind === 'audiooutput');

      setDevices(audioDevices, videoDevices, audioOutputDevices);

      // Останавливаем тестовый стрим
      stream.getTracks().forEach((track) => track.stop());

      setLoading(false);
    } catch (err: any) {
      console.error('Failed to get media devices:', err);
      setError(err.message || 'Failed to access media devices');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevices();

    // Слушаем изменения устройств (только если API доступен)
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      const handleDeviceChange = () => {
        loadDevices();
      };

      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

      return () => {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      };
    }
  }, []);

  return { loading, error, reload: loadDevices };
};

export const getUserMedia = async (
  audioDeviceId?: string,
  videoDeviceId?: string,
  audioEnabled: boolean = true,
  videoEnabled: boolean = true
): Promise<MediaStream> => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    throw new Error(
      'WebRTC не поддерживается. Используйте HTTPS или localhost для доступа к камере и микрофону.'
    );
  }

  // Всегда запрашиваем треки, но отключаем их если пользователь выключил устройство
  // Это позволяет включить их позже без повторного запроса разрешений
  const constraints: MediaStreamConstraints = {
    audio: audioDeviceId
      ? { deviceId: { exact: audioDeviceId } }
      : true,
    video: videoDeviceId
      ? { deviceId: { exact: videoDeviceId }, width: 1280, height: 720 }
      : { width: 1280, height: 720 },
  };

  const stream = await navigator.mediaDevices.getUserMedia(constraints);

  // Отключаем треки если пользователь выключил устройство
  if (!audioEnabled) {
    stream.getAudioTracks().forEach(track => {
      track.enabled = false;
    });
  }
  
  if (!videoEnabled) {
    stream.getVideoTracks().forEach(track => {
      track.enabled = false;
    });
  }

  return stream;
};
